/**
 * Returns the unix epoch date.
 */
export function unixEpoch() {
  return new Date(0)
}

/**
 * Parses a string into a date if it's a string.
 * If `undefined, returns `undefined`. Intended for use in `Model.parse`.
 * If `null`, returns `null`.
 * If an invalid date, returns `undefined`.
 * @param value
 */
export function maybeParseDate<T extends string | null | undefined>(
  value: T,
): T extends null ? Date | undefined | null : Date | undefined {
  // For some reason, we have to force these conversions.
  // The caller signature is correct though.
  if (value === null) {
    // @ts-ignore: this is correct
    return null
  }

  if (value === '' || value === undefined) {
    return undefined
  }

  const result = new Date(value)
  if (Number.isNaN(result.getTime())) {
    return undefined!
  }

  return result
}

/**
 * Same as {@link maybeParseDate} but falls back to the given value, or
 * the unix epoch if no fallback specified.
 *
 * @param value
 * @param fallback
 */
export function safeParseDate(
  value: string | null | undefined,
  fallback?: Date,
): Date {
  return maybeParseDate(value) ?? fallback ?? unixEpoch()
}

/**
 * Common date formatters.
 */
export const dateFormatters = {
  dateMedium: new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
  }),
  dateLong: new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
  }),
  dateFull: new Intl.DateTimeFormat('en-US', {
    dateStyle: 'full',
  }),
  dateTimeDefault: new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }),
  dateTimeFull: new Intl.DateTimeFormat('en-US', {
    dateStyle: 'full',
    timeStyle: 'short',
  }),
  timeDefault: new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }),
  dateAmerican: new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }),
}

/**
 * Parses a date in the `MM/dd/yyyy` format into a {@link Date} with local time
 * at midnight.
 *
 * @param str
 */
export function parseAmericanDateParts(str: string) {
  const [, monthStr, dayStr, yearStr] =
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(str.trim()) ?? []

  if (!monthStr || !dayStr || !yearStr) {
    return null
  }

  return {
    year: Number(yearStr),
    month: Number(monthStr) - 1,
    day: Number(dayStr),
  }
}

/**
 * Parses a date in the `MM/dd/yyyy` format into a {@link Date} with local time
 * at midnight.
 *
 * @param str
 * @param pivot If specified, and the parsed year is 2 digits, determines the
 * century based on  whether the year value is less than or greater than the
 * pivot. Typically, `49` is used.
 */
export function parseAmericanDate(str: string, pivot?: number): Date | null {
  const parsed = parseAmericanDateParts(str)
  if (!parsed) {
    return null
  }

  const { year, month, day } = parsed
  const date = new Date()

  if (year <= 99) {
    if (pivot === undefined) {
      // Pivoting is disabled
      return null
    }

    // I'll bump these numbers when I retire LMAO
    const pivoted = year <= pivot ? 2000 + year : 1900 + year
    date.setFullYear(pivoted)
  } else {
    date.setFullYear(year)
  }

  date.setMonth(month)
  date.setDate(day)
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)
  return date
}
